import { ArrowsClockwiseIcon } from '@phosphor-icons/react';
import { useState } from 'react';

import { ICON_WEIGHT } from '@/components/icon';
import { Button } from '@/components/ui/button';
import { useDesktopUpdate } from '@/desktop';
import { applyUpdate, usePwaUpdate } from '@/pwa';

/**
 * A new version, offered and never forced: the web app's waiting service
 * worker, or the desktop app's downloaded manifest. Nothing changes until
 * the pilot presses the button.
 */
export function UpdateBanner() {
  const waiting = usePwaUpdate((state) => state.waiting);
  const desktop = useDesktopUpdate((state) => state.available);
  const [installing, setInstalling] = useState(false);
  const [failed, setFailed] = useState(false);

  if (waiting === null && desktop === null) {
    return null;
  }

  let text: string;
  let action: { label: string; run: () => void } | null;
  if (desktop !== null) {
    text = desktop.covered
      ? `Version ${desktop.version} available.`
      : `Version ${desktop.version} available. Not covered by the licence.`;
    action = desktop.covered
      ? {
          label: installing ? 'Installing' : 'Install and restart',
          run: () => {
            setInstalling(true);
            setFailed(false);
            desktop.install().catch(() => {
              setInstalling(false);
              setFailed(true);
            });
          },
        }
      : null;
  } else {
    text = 'Update available.';
    action = { label: 'Reload', run: applyUpdate };
  }

  return (
    <aside
      aria-label="Update"
      className="border-border bg-surface rounded-container mb-6 flex items-center gap-3 border px-4 py-3 text-sm print:hidden"
    >
      <ArrowsClockwiseIcon
        aria-hidden="true"
        weight={ICON_WEIGHT}
        className="text-accent size-4 shrink-0"
      />
      <span className="flex-1">
        {text}
        {failed ? <span className="text-danger"> Update could not be installed.</span> : null}
      </span>
      {action === null ? null : (
        <Button size="sm" disabled={installing} onClick={action.run}>
          {action.label}
        </Button>
      )}
    </aside>
  );
}
